import { hasPermission } from 'app/core/permissions';
import { getUSHolidaysSafe } from 'app/helpers/parseDate';
import { AppNS, Dcra } from 'app/index.d';
import moment from 'moment-timezone';

/**
 * An inspection is editable when
 * inspection is not cancelled and
 * user is `backoffice`
 * or the user is `client` and the current date is before at least 1 day the inspection date
 * @param inspection
 * @param userType
 * @returns
 */
export const isInspectionEditable = (inspection: Dcra.Inspection, user: AppNS.User): boolean => {
  return hasPermission(user, 'dobInspections', 'update', inspection);
};

/**
 * An inspection permits list is editable when
 * inspection is not cancelled and
 * user is `backoffice`
 * or user is `inspector` and the current date is the inspection date
 * or the user is `client` and the current date is before at least 1 day the inspection date
 * @param inspection
 * @param user
 * @returns
 */
export const isInspectionPermitsEditable = (inspection: Dcra.Inspection, user: AppNS.User): boolean => {
  return hasPermission(user, 'dobInspections', 'editPermits', inspection);
};

/**
 * @param inspection
 * @param user
 * @returns
 */
export const isInspectionCancellable = (inspection: Dcra.Inspection, user: AppNS.User): boolean => {
  return hasPermission(user, 'dobInspections', 'cancel', inspection);
};

export const isInspectionCancellableByState = (inspection: Dcra.Inspection): boolean => {
  if (inspection.state !== 'upcoming') {
    return false;
  }

  if (inspection.checkInAt) {
    return false;
  }

  return true;
};

export const checkIsDateBefore = (date: string): boolean => {
  return moment()
    .tz('America/New_York')
    .isBefore(moment.tz(`${date} 14:00:00`, 'America/New_York').add(-1, 'days'));
};

export const checkIsSameDay = (date: string): boolean => {
  return moment().tz('America/New_York').isSame(moment(date).tz('America/New_York'), 'day');
};

export const checkIsSameDate = (date1: string, date2: string): boolean => {
  return moment(date1).isSame(moment(date2), 'day');
};

export const getFirstAvailableDay = (): moment.Moment | null => {
  const today = moment().tz('America/New_York');
  const isAfter14 = today.isAfter(moment.tz(`${today.format('YYYY-MM-DD')} 14:00:00`, 'America/New_York'));
  const isAvailable = checkIsAvailableDay(today);

  if (isAfter14 || !isAvailable) {
    let nextDay = today.clone();
    do {
      nextDay = nextDay.clone().add(1, 'days');
    } while (!checkIsAvailableDay(nextDay));
    return nextDay;
  }

  return null;
};

export const checkIsAvailableDay = (current: moment.Moment): boolean => {
  const HOLIDAYS = getUSHolidaysSafe();
  const isHoliday = HOLIDAYS.includes(current.format('YYYY-MM-DD'));
  const isWeekend = current.isoWeekday() >= 6;

  if (isHoliday || isWeekend) {
    return false;
  }

  return true;
};
